import { useState } from 'react';
import { Message, Icon } from 'semantic-ui-react';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
// getTrainingPlanNextUp is an optional CALLBACK (not a value): it's invoked at
// render time after loadNewAssessment resolves, so the just-graded session is
// already in the store when plan progress is computed (the parent recorder is
// not an observer, so a precomputed prop would be stale).
const MurphyStatusDisplay = ({ videoStatus, error, loadNewAssessment, getTrainingPlanNextUp }) => {
    const [newAssessmentLoaded, setNewAssessmentLoaded] = useState(false);

    useEffect(() => {
        if (videoStatus === 'graded') {
            loadNewAssessment().then(() => {
                setNewAssessmentLoaded(true);
            });
        } else {
            setNewAssessmentLoaded(false);
        }
    }, [videoStatus, loadNewAssessment]);

    if (error === 'permanently_failed') {
        return <ErrorDisplay message="Murphy was unable to grade this practice session. Please try recording again. If the issue persists, contact support." />;
    }

    if (error === 'upload_failed') {
        return <ErrorDisplay message="Your recording didn't upload, so it hasn't been saved. This is usually a camera/microphone permission or network issue. Please make sure your camera and mic are allowed, then record again. If the issue persists, contact support." />;
    }

    // NOTE: these device/permission variants MUST stay above the generic `if (error)` below.
    // They are keyed to specific getUserMedia failure causes (see classifyMediaError in
    // NewRecordPresentation). Do not merge their copy into the generic grading-delay branch —
    // that branch is for genuine grade timeouts and has overwritten device guidance before.
    if (error === 'device_not_found') {
        return <ErrorDisplay message="We couldn't detect a working microphone or camera. Please check that they're connected and that microphone/camera access is enabled in your browser and your operating system settings, then click Start to try again." />;
    }

    if (error === 'permission_denied') {
        return <ErrorDisplay message="Microphone and camera access is blocked. Please allow access for this site in your browser's permission settings (and check your operating system's privacy settings), then try again." />;
    }

    if (error === 'mic_muted') {
        return <ErrorDisplay message="Your microphone is muted, so the meeting can't start. Unmute it with the Mute button below, check your headset or laptop's mute switch, and check your system sound settings. Then click Start Meeting to try again." />;
    }

    if (error === 'recording_failed') {
        return <ErrorDisplay message="We couldn't start recording. Please check that your microphone and camera are working, close any other apps that might be using them (Zoom, Teams, etc.), and try again. If this continues, try a different browser." />;
    }

    if (error) {
        return <ErrorDisplay message="Murphy is taking longer than usual to grade your practice. Your video has been saved and Murphy will continue working on it. Please check back shortly for your feedback." />;
    }

    if (!videoStatus) {
        return null;
    }

    let message = '';

    if (videoStatus === 'loading') {
        message = 'Murphy is preparing new questions from your document...';
    } else if (videoStatus === 'uploading') {
        message = 'Uploading Video...';
    } else if (videoStatus === 'transcribing') {
        message = 'Your video is currently being transcribed.  This can take several minutes.';
    } else if (videoStatus === 'grading' || (videoStatus === 'graded' && !newAssessmentLoaded)) {
        message = 'Murphy is now grading your video.';
    } else if (videoStatus === 'graded' && newAssessmentLoaded) {
        const planNextUp = typeof getTrainingPlanNextUp === 'function' ? getTrainingPlanNextUp() : null;
        message = <>
            Murphy is all finished.  You can view your report{' '}
            <Link to="/my-assessments-report" style={{ textDecoration: 'underline', fontWeight: 'bold', color: '#06718f' }}>here</Link>.
            {planNextUp && (
                <div style={{ marginTop: '10px' }}>
                    {planNextUp.todayComplete ? (
                        <>That's Day {planNextUp.dayNumber} of your Training Plan done. See you tomorrow!</>
                    ) : (
                        <>
                            Training Plan Day {planNextUp.dayNumber}: {planNextUp.doneCount} of {planNextUp.totalCount} done today.
                            {planNextUp.nextTitle && (
                                <>
                                    {' '}Next up: <strong>{planNextUp.nextTitle}</strong>
                                    {planNextUp.nextMinutes ? ` (~${planNextUp.nextMinutes} min)` : ''}.
                                </>
                            )}
                        </>
                    )}
                    <div style={{ marginTop: '10px' }}>
                        <Link
                            to="/dashboard"
                            style={{ display: 'inline-block', padding: '8px 14px', background: '#0784a7', color: '#ffffff', borderRadius: '6px', fontWeight: 600, textDecoration: 'none', fontFamily: 'Montserrat' }}
                        >
                            Back to your Training Plan
                        </Link>
                    </div>
                </div>
            )}
        </>;
    }

    return (
        <div className="murphy-status">
            {videoStatus !== 'graded' && videoStatus !== '' && (
                <div className="loading-icon">
                    <Icon name='circle notched' loading />
                </div>
            )}
            <div className="status-content">
                <div className="status-message">{message}</div>
            </div>
        </div>
    );
};

export default MurphyStatusDisplay;